import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  PiChartLineDuotone,
  PiCrownDuotone,
  PiFireDuotone,
  PiMedalDuotone,
  PiSparkleDuotone,
  PiTargetDuotone,
  PiTrendUpDuotone,
} from 'react-icons/pi';
import { getRoom, getRoomMembers, getRoomStatistics } from '../api/roomService';
import { accessAwareMessage } from '../api/api';
import Badge from '../shared/common/Badge';
import Breadcrumb from '../shared/common/Breadcrumb';
import Button from '../shared/common/Button';
import Card from '../shared/common/Card';
import Select from '../shared/common/Select';
import StatCard from '../shared/common/StatCard';
import { SkeletonGrid } from '../shared/common/Skeleton';
import { publicUserName } from '../shared/utils/userDisplay';
import { useDelayedLoading } from '../shared/utils/useDelayedLoading';

const EMPTY_SUMMARY = Object.freeze({
  totalScore: 0,
  currentUserScore: 0,
  roomAverageScore: 0,
  leaderScore: 0,
  totalResolvedBets: 0,
  roomAccuracyPercent: 0,
  currentUserAccuracyPercent: 0,
  currentUserAverageMultiplier: 1,
});
const MAX_TIMELINE_BARS = 12;
const MAX_DETAIL_ROWS = 14;
const PERCENT_FRACTION_DIGITS = 0;
const SCORE_FRACTION_DIGITS = 1;
const STATISTICS_LOAD_ERROR = 'Не удалось загрузить статистику комнаты';
const MAIN_DETAIL_TYPE = 'MAIN';
const SIDE_BET_DETAIL_TYPE = 'SIDE_BET';
const EXACT_SCORE_RULE_TYPE = 'EXACT_SCORE';
const SCORE_WIN_THRESHOLD = 1;
const STAT_GRID_STYLE = Object.freeze({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(190px, 100%), 1fr))', gap: 'var(--bm-space-md)' });
const ACHIEVEMENT_GRID_STYLE = Object.freeze({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 'var(--bm-space-md)' });
const COMPACT_DETAIL_GRID_STYLE = Object.freeze({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 'var(--bm-space-md)' });
const ROOM_BREAKDOWN_GRID_STYLE = Object.freeze({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 'var(--bm-space-md)' });
const PLAYER_STATS_GRID_STYLE = Object.freeze({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 'var(--bm-space-sm)' });
const GLOBAL_FILTER_CARD_STYLE = Object.freeze({ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'end' });
const PLAYER_SELECT_WIDTH_STYLE = Object.freeze({ width: 260, maxWidth: '100%' });
const OWN_TIMELINE_BAR_WIDTH = 28;
const OTHER_TIMELINE_BAR_WIDTH = 18;
const TIMELINE_BAR_MAX_HEIGHT = 130;
const OWN_TIMELINE_BAR_MIN_HEIGHT = 10;
const OTHER_TIMELINE_BAR_MIN_HEIGHT = 8;

const buildRoomPath = (roomId) => `/rooms/${roomId}`;
const numberValue = (value) => Number(value ?? 0);
const stringId = (value) => (value == null ? '' : String(value));
const formatPercent = (value) => `${numberValue(value).toFixed(PERCENT_FRACTION_DIGITS)}%`;
const formatDecimal = (value) => numberValue(value).toFixed(SCORE_FRACTION_DIGITS);
const scoreLabel = (value) => `${numberValue(value) > 0 ? '+' : ''}${numberValue(value)}`;
const formatDate = (value) => {
  if (!value) return '—';
  return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};
const membersByUserId = (members) => new Map((Array.isArray(members) ? members : [])
  .map((member) => [stringId(member.userId), member]));
const memberName = (membersMap, userId) => publicUserName(membersMap.get(stringId(userId)) || { userId });
const lastTimelineBuckets = (timeline) => (Array.isArray(timeline) ? timeline.slice(-MAX_TIMELINE_BARS) : []);
const maxAbsScoreDelta = (buckets, key = 'scoreDelta') => Math.max(1, ...buckets.map((bucket) => Math.abs(numberValue(bucket[key]))));
const participantById = (leaderboard, userId) => leaderboard.find((participant) => stringId(participant.userId) === stringId(userId));
const detailsForUser = (details, userId) => (Array.isArray(details) ? details : [])
  .filter((detail) => stringId(detail.userId) === stringId(userId));
const detailsByType = (details, type) => details.filter((detail) => detail.type === type).slice(0, MAX_DETAIL_ROWS);
const detailTitle = (detail) => detail.eventTitle || detail.title || `Пари #${detail.challengeId}`;
const detailActualResult = (detail) => detail.actualResult || detail.result || '—';
const detailPrediction = (detail) => detail.prediction || '—';
const sideBetQuestion = (detail) => detail.title || 'Side bet';
const sideBetSelectedResult = (detail) => detail.prediction || '—';
const sideBetActualResult = (detail) => detail.result || '—';
const timelineForUser = (timeline, userId) => lastTimelineBuckets((Array.isArray(timeline) ? timeline : [])
  .filter((bucket) => stringId(bucket.userId) === stringId(userId)));
const otherTimelineBuckets = (timeline, userId, membersMap) => (Array.isArray(timeline) ? timeline : [])
  .filter((bucket) => stringId(bucket.userId) !== stringId(userId))
  .map((bucket) => ({
    ...bucket,
    userId: stringId(bucket.userId),
    playerName: memberName(membersMap, bucket.userId),
  }))
  .sort((left, right) => left.date.localeCompare(right.date) || left.playerName.localeCompare(right.playerName));
const timelineBucketsForDate = (buckets, date) => buckets.filter((bucket) => bucket.date === date);
const timelineDates = (ownBuckets, otherBuckets) => [...new Set([
  ...ownBuckets.map((bucket) => bucket.date),
  ...otherBuckets.map((bucket) => bucket.date),
])].sort((left, right) => left.localeCompare(right));
const timelineDisplayBuckets = (ownBuckets, otherBuckets) => {
  const ownBucketsByDate = new Map(ownBuckets.map((bucket) => [bucket.date, bucket]));
  return lastTimelineBuckets(timelineDates(ownBuckets, otherBuckets)
    .map((date) => ownBucketsByDate.get(date) || { date, scoreDelta: 0, cumulativeScore: 0, empty: true }));
};
const timelineBucketHeight = (scoreDelta, maxDelta, minHeight) => Math.max(
  minHeight,
  Math.round((Math.abs(numberValue(scoreDelta)) / maxDelta) * TIMELINE_BAR_MAX_HEIGHT),
);
const selectedParticipantStats = (participant) => participant || {
  totalScore: 0,
  mainScore: 0,
  sideBetScore: 0,
  mainBetsCount: 0,
  sideBetsCount: 0,
  resolvedBetsCount: 0,
  accuracyPercent: 0,
  averageMultiplier: 1,
};
const selectedPlayerName = (membersMap, userId) => (userId ? memberName(membersMap, userId) : 'Игрок');
const dateCompare = (left, right) => (left || '').localeCompare(right || '');
const isWinningDetail = (detail) => numberValue(detail.score) >= SCORE_WIN_THRESHOLD;
const isExactScoreDetail = (detail) => detail.type === MAIN_DETAIL_TYPE && detail.matchedRuleType === EXACT_SCORE_RULE_TYPE;
const selectedAchievements = (details) => {
  const chronologicalDetails = [...details].sort((left, right) => dateCompare(left.date, right.date));
  const mainDetails = details.filter((detail) => detail.type === MAIN_DETAIL_TYPE);
  const sideBetDetails = details.filter((detail) => detail.type === SIDE_BET_DETAIL_TYPE);
  const longestWinStreak = chronologicalDetails.reduce((accumulator, detail) => {
    const nextStreak = isWinningDetail(detail) ? accumulator.current + 1 : 0;
    return { current: nextStreak, best: Math.max(accumulator.best, nextStreak) };
  }, { current: 0, best: 0 }).best;
  const exactScoreCount = mainDetails.filter(isExactScoreDetail).length;
  const bestSingleScore = chronologicalDetails.reduce((best, detail) => Math.max(best, numberValue(detail.score)), 0);
  const sideBetHitCount = sideBetDetails.filter(isWinningDetail).length;

  return [
    {
      key: 'streak',
      icon: <PiFireDuotone size={22} />,
      value: `${longestWinStreak} подряд`,
      title: 'Серия угаданных исходов',
      description: 'Самая длинная цепочка ставок с плюсовыми очками.',
      iconColor: 'var(--bm-accent-red)',
      iconBg: 'rgba(255, 84, 112, 0.12)',
    },
    {
      key: 'exact',
      icon: <PiTargetDuotone size={22} />,
      value: exactScoreCount,
      title: 'Точные прогнозы',
      description: 'Основные ставки, где сработало правило точного счёта.',
      iconColor: 'var(--bm-accent-green)',
      iconBg: 'rgba(0, 214, 143, 0.12)',
    },
    {
      key: 'best-score',
      icon: <PiCrownDuotone size={22} />,
      value: scoreLabel(bestSingleScore),
      title: 'Лучший одиночный занос',
      description: 'Максимум очков за одну завершённую ставку.',
      iconColor: 'var(--bm-accent-gold)',
      iconBg: 'rgba(255, 209, 102, 0.12)',
    },
    {
      key: 'side-bet-hits',
      icon: <PiSparkleDuotone size={22} />,
      value: sideBetHitCount,
      title: 'Side bet попадания',
      description: 'Особые исходы, принесшие игроку очки.',
      iconColor: 'var(--bm-accent-cyan)',
      iconBg: 'rgba(59, 130, 246, 0.12)',
    },
  ];
};

const ScoreDetailCard = ({ children, detail }) => (
  <div
    key={`${detail.type}-${detail.id}`}
    style={{
      display: 'grid',
      gap: 8,
      padding: '10px 0',
      borderBottom: '1px solid var(--bm-border)',
    }}
  >
    {children}
  </div>
);

const DetailValueRow = ({ label, value, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
    <span className="bm-text-secondary">{label}</span>
    {children || <strong style={{ textAlign: 'right', wordBreak: 'break-word' }}>{value}</strong>}
  </div>
);

const DetailListCard = ({ title, details, children }) => (
  <Card variant="glass" style={{ display: 'grid', gap: 10 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <h3 className="bm-h4">{title}</h3>
      <Badge status="primary">{details.length}</Badge>
    </div>
    {details.length === 0 ? <p className="bm-text-secondary bm-text-sm">Пока нет начислений.</p> : details.map(children)}
  </Card>
);

const MainDetailList = ({ title, details }) => (
  <DetailListCard title={title} details={details}>
    {(detail) => (
      <ScoreDetailCard key={`${detail.type}-${detail.id}`} detail={detail}>
        <div className="bm-font-medium" style={{ wordBreak: 'break-word' }}>{detailTitle(detail)}</div>
        <div className="bm-text-sm" style={{ display: 'grid', gap: 6 }}>
          <DetailValueRow label="Фактический итог" value={detailActualResult(detail)} />
          <DetailValueRow label="Прогноз" value={detailPrediction(detail)} />
          <DetailValueRow label="Заработанные очки">
            <strong style={{ color: numberValue(detail.score) > 0 ? 'var(--bm-accent-green)' : 'var(--bm-text-muted)' }}>
              {scoreLabel(detail.score)}
            </strong>
          </DetailValueRow>
        </div>
      </ScoreDetailCard>
    )}
  </DetailListCard>
);

const SideBetDetailList = ({ title, details }) => (
  <DetailListCard title={title} details={details}>
    {(detail) => (
      <ScoreDetailCard key={`${detail.type}-${detail.id}`} detail={detail}>
        <div className="bm-font-medium" style={{ wordBreak: 'break-word' }}>{detailTitle(detail)}</div>
        <div className="bm-text-sm" style={{ display: 'grid', gap: 6 }}>
          <DetailValueRow label="Формулировка side bet" value={sideBetQuestion(detail)} />
          <DetailValueRow label="Выбранный результат" value={sideBetSelectedResult(detail)} />
          <DetailValueRow label="Фактический результат" value={sideBetActualResult(detail)} />
          <DetailValueRow label="Заработанные очки">
            <strong style={{ color: numberValue(detail.score) > 0 ? 'var(--bm-accent-green)' : 'var(--bm-text-muted)' }}>
              {scoreLabel(detail.score)}
            </strong>
          </DetailValueRow>
        </div>
      </ScoreDetailCard>
    )}
  </DetailListCard>
);

const AchievementCard = ({ achievement }) => (
  <Card variant="glass" style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          width: 42,
          height: 42,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 'var(--bm-radius-lg)',
          color: achievement.iconColor,
          background: achievement.iconBg,
        }}
      >
        {achievement.icon}
      </div>
      <div>
        <div style={{ fontWeight: 900, fontSize: 22 }}>{achievement.value}</div>
        <h3 className="bm-h4">{achievement.title}</h3>
      </div>
    </div>
    <p className="bm-text-secondary bm-text-sm">{achievement.description}</p>
  </Card>
);

const IndividualTimeline = ({ ownBuckets, otherBuckets, overlayOthers }) => {
  const visibleOtherBuckets = overlayOthers ? otherBuckets : [];
  const displayBuckets = overlayOthers ? timelineDisplayBuckets(ownBuckets, visibleOtherBuckets) : ownBuckets;
  const maxDelta = maxAbsScoreDelta([...ownBuckets, ...visibleOtherBuckets]);
  return (
    <div style={{ display: 'flex', alignItems: 'end', gap: 10, minHeight: 170, overflowX: 'auto', paddingTop: 8 }}>
      {displayBuckets.length === 0 ? (
        <p className="bm-text-secondary bm-text-sm" style={{ padding: '20px 0' }}>У игрока пока нет динамики.</p>
      ) : displayBuckets.map((bucket) => {
        const otherBucketsForDate = timelineBucketsForDate(visibleOtherBuckets, bucket.date);
        const ownHeight = timelineBucketHeight(bucket.scoreDelta, maxDelta, OWN_TIMELINE_BAR_MIN_HEIGHT);
        return (
          <div key={bucket.date} style={{ minWidth: 78, display: 'grid', gap: 6, justifyItems: 'center' }}>
            <div className="bm-text-sm" style={{ fontWeight: 700 }}>{scoreLabel(bucket.scoreDelta)}</div>
            <div style={{ display: 'flex', alignItems: 'end', gap: 5 }}>
              <div
                title={bucket.empty ? 'Игрок: нет начислений' : `Игрок: ${bucket.cumulativeScore}`}
                style={{
                  width: OWN_TIMELINE_BAR_WIDTH,
                  height: ownHeight,
                  borderRadius: '10px 10px 4px 4px',
                  background: 'linear-gradient(180deg, var(--bm-accent-primary), rgba(124,92,255,0.35))',
                  opacity: bucket.empty ? 0.2 : 1,
                }}
              />
              {overlayOthers && otherBucketsForDate.map((otherBucket) => (
                <div
                  key={`${otherBucket.userId}-${otherBucket.date}`}
                  title={`${otherBucket.playerName}: ${scoreLabel(otherBucket.scoreDelta)}`}
                  style={{
                    width: OTHER_TIMELINE_BAR_WIDTH,
                    height: timelineBucketHeight(otherBucket.scoreDelta, maxDelta, OTHER_TIMELINE_BAR_MIN_HEIGHT),
                    borderRadius: '8px 8px 4px 4px',
                    background: 'linear-gradient(180deg, var(--bm-accent-gold), rgba(255,209,102,0.3))',
                    opacity: 0.9,
                  }}
                />
              ))}
            </div>
            <div className="bm-text-secondary bm-text-sm">{formatDate(bucket.date)}</div>
          </div>
        );
      })}
    </div>
  );
};

export default function RoomStatisticsPage() {
  const { roomId } = useParams();
  const [room, setRoom] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [overlayOthers, setOverlayOthers] = useState(false);
  const [loading, setLoading] = useState(true);
  const showSkeleton = useDelayedLoading(loading);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'BetMate — Статистика комнаты';
    Promise.all([getRoom(roomId), getRoomStatistics(roomId), getRoomMembers(roomId)])
      .then(([roomData, statisticsData, membersData]) => {
        setRoom(roomData);
        setStatistics(statisticsData);
        setMembers(Array.isArray(membersData) ? membersData : []);
        setSelectedUserId(stringId(statisticsData?.currentUserId));
      })
      .catch((err) => setError(accessAwareMessage(err, STATISTICS_LOAD_ERROR)))
      .finally(() => setLoading(false));
  }, [roomId]);

  const summary = statistics?.summary || EMPTY_SUMMARY;
  const leaderboard = Array.isArray(statistics?.leaderboard) ? statistics.leaderboard : [];
  const timeline = lastTimelineBuckets(statistics?.timeline);
  const maxDelta = maxAbsScoreDelta(timeline);
  const details = Array.isArray(statistics?.details) ? statistics.details : [];
  const participantTimeline = Array.isArray(statistics?.participantTimeline) ? statistics.participantTimeline : [];
  const membersMap = useMemo(() => membersByUserId(members), [members]);
  const selectedParticipant = selectedParticipantStats(participantById(leaderboard, selectedUserId));
  const selectedDetails = detailsForUser(details, selectedUserId);
  const selectedMainDetails = detailsByType(selectedDetails, MAIN_DETAIL_TYPE);
  const selectedSideBetDetails = detailsByType(selectedDetails, SIDE_BET_DETAIL_TYPE);
  const achievements = selectedAchievements(selectedDetails);
  const selectedTimeline = timelineForUser(participantTimeline, selectedUserId);
  const otherTimeline = otherTimelineBuckets(participantTimeline, selectedUserId, membersMap);
  const selectedName = selectedPlayerName(membersMap, selectedUserId);
  const userOptions = leaderboard.map((participant) => ({
    value: stringId(participant.userId),
    label: memberName(membersMap, participant.userId),
  }));

  return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <Breadcrumb
        items={[
          { label: 'Комнаты', to: '/rooms' },
          { label: room?.name || 'Комната', to: buildRoomPath(roomId) },
          { label: 'Статистика' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="bm-h2">Статистика комнаты</h1>
          <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
            {room?.name || 'Комната'}: динамика очков, индивидуальные начисления и сравнение участников.
          </p>
        </div>
      </div>

      {error && <div className="bm-auth-error">{error}</div>}

      {showSkeleton ? (
        <SkeletonGrid count={6} />
      ) : (
        <>
          <Card variant="glass" style={GLOBAL_FILTER_CARD_STYLE}>
            <div>
              <h3 className="bm-h4">Разрез статистики</h3>
              <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
                Выбран игрок: {selectedName}. Ниже его очки, динамика, результаты и выбранные исходы.
              </p>
            </div>
            <div style={PLAYER_SELECT_WIDTH_STYLE}>
              <Select value={selectedUserId} onChange={setSelectedUserId} options={userOptions} />
            </div>
          </Card>

          <section style={STAT_GRID_STYLE}>
            <StatCard icon={<PiTrendUpDuotone size={22} />} value={selectedParticipant.totalScore} label="Очки игрока" iconColor="var(--bm-accent-primary)" iconBg="rgba(124, 92, 255, 0.14)" />
            <StatCard icon={<PiChartLineDuotone size={22} />} value={summary.roomAverageScore} label="Среднее по комнате" iconColor="var(--bm-accent-cyan)" iconBg="rgba(59, 130, 246, 0.12)" />
            <StatCard icon={<PiMedalDuotone size={22} />} value={summary.leaderScore} label="Очки лидера" iconColor="var(--bm-accent-gold)" iconBg="rgba(255, 209, 102, 0.12)" />
            <StatCard icon={<PiTargetDuotone size={22} />} value={formatPercent(selectedParticipant.accuracyPercent)} label="Точность игрока" iconColor="var(--bm-accent-green)" iconBg="rgba(0, 214, 143, 0.12)" />
          </section>

          <section style={{ display: 'grid', gap: 'var(--bm-space-md)' }}>
            <div>
              <h2 className="bm-h3">Ачивки игрока</h2>
              <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
                Быстрый brag-board для {selectedName}: серии, точные счёты и лучшие попадания.
              </p>
            </div>
            <div style={ACHIEVEMENT_GRID_STYLE}>
              {achievements.map((achievement) => <AchievementCard key={achievement.key} achievement={achievement} />)}
            </div>
          </section>

          <Card variant="glass" style={{ display: 'grid', gap: 'var(--bm-space-md)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 className="bm-h4">Динамика комнаты</h3>
                <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>Последние дни с завершёнными основными ставками и side bets.</p>
              </div>
              <Badge status="primary">{summary.totalResolvedBets} ставок</Badge>
            </div>
            {timeline.length === 0 ? (
              <p className="bm-text-secondary bm-text-sm" style={{ textAlign: 'center', padding: '20px 0' }}>Динамика появится после завершения первых пари.</p>
            ) : (
              <div style={{ display: 'flex', alignItems: 'end', gap: 10, minHeight: 180, overflowX: 'auto', paddingTop: 8 }}>
                {timeline.map((bucket) => {
                  const delta = numberValue(bucket.scoreDelta);
                  const height = Math.max(10, Math.round((Math.abs(delta) / maxDelta) * 140));
                  const positive = delta >= 0;
                  return (
                    <div key={bucket.date} style={{ minWidth: 72, display: 'grid', gap: 6, justifyItems: 'center' }}>
                      <div className="bm-text-sm" style={{ fontWeight: 700, color: positive ? 'var(--bm-accent-green)' : 'var(--bm-accent-red)' }}>{scoreLabel(delta)}</div>
                      <div style={{ width: 34, height, borderRadius: '10px 10px 4px 4px', background: positive ? 'linear-gradient(180deg, var(--bm-accent-green), rgba(0,214,143,0.35))' : 'linear-gradient(180deg, var(--bm-accent-red), rgba(255,84,112,0.35))' }} />
                      <div className="bm-text-secondary bm-text-sm">{formatDate(bucket.date)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card variant="glass" style={{ display: 'grid', gap: 'var(--bm-space-md)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
              <div>
                <h3 className="bm-h4">Индивидуальная статистика</h3>
                <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>Динамика и начисления игрока {selectedName}; можно наложить остальных для сравнения.</p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant={overlayOthers ? 'primary' : 'secondary'} size="sm" onClick={() => setOverlayOthers((prev) => !prev)}>
                  {overlayOthers ? 'Скрыть других' : 'Наложить других'}
                </Button>
              </div>
            </div>

            <section style={PLAYER_STATS_GRID_STYLE}>
              <StatCard icon={<PiTrendUpDuotone size={18} />} value={selectedParticipant.totalScore} label="Очки игрока" />
              <StatCard icon={<PiTargetDuotone size={18} />} value={formatPercent(selectedParticipant.accuracyPercent)} label="Точность" />
              <StatCard icon={<PiChartLineDuotone size={18} />} value={selectedParticipant.mainScore} label="Основной исход" />
              <StatCard icon={<PiMedalDuotone size={18} />} value={selectedParticipant.sideBetScore} label="Side bet" />
            </section>

            <IndividualTimeline ownBuckets={selectedTimeline} otherBuckets={otherTimeline} overlayOthers={overlayOthers} />
          </Card>

          <div style={COMPACT_DETAIL_GRID_STYLE}>
            <MainDetailList title="Очки за основной исход" details={selectedMainDetails} />
            <SideBetDetailList title="Очки за side bet" details={selectedSideBetDetails} />
          </div>

          {overlayOthers && (
            <div style={COMPACT_DETAIL_GRID_STYLE}>
              <MainDetailList title="Другие игроки: основной исход" details={detailsByType(details.filter((detail) => stringId(detail.userId) !== stringId(selectedUserId)), MAIN_DETAIL_TYPE)} />
              <SideBetDetailList title="Другие игроки: side bet" details={detailsByType(details.filter((detail) => stringId(detail.userId) !== stringId(selectedUserId)), SIDE_BET_DETAIL_TYPE)} />
            </div>
          )}

          <div style={ROOM_BREAKDOWN_GRID_STYLE}>
            <Card variant="glass" style={{ display: 'grid', gap: 12 }}>
              <h3 className="bm-h4">Leaderboard за всё время</h3>
              {leaderboard.length === 0 ? <p className="bm-text-secondary bm-text-sm">Пока нет данных по участникам.</p> : leaderboard.map((participant) => (
                <div key={participant.userId} style={{ display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr) auto', gap: 12, alignItems: 'center', padding: 12, border: '1px solid var(--bm-border)', borderRadius: 'var(--bm-radius-lg)' }}>
                  <div style={{ fontWeight: 800, color: 'var(--bm-accent-primary)' }}>#{participant.rank}</div>
                  <div style={{ minWidth: 0 }}>
                    <div className="bm-font-medium bm-truncate">{memberName(membersMap, participant.userId)}</div>
                    <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 2 }}>{participant.resolvedBetsCount} ставок · точность {formatPercent(participant.accuracyPercent)} · риск ×{formatDecimal(participant.averageMultiplier)}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 800 }}>{participant.totalScore}</div>
                    <div className="bm-text-secondary bm-text-sm">очк.</div>
                  </div>
                </div>
              ))}
            </Card>

            <Card variant="glass" style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
              <h3 className="bm-h4">Разбор комнаты</h3>
              <div className="bm-text-sm" style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><span className="bm-text-secondary">Все очки</span><strong>{summary.totalScore}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><span className="bm-text-secondary">Точность комнаты</span><strong>{formatPercent(summary.roomAccuracyPercent)}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><span className="bm-text-secondary">Мой средний риск</span><strong>×{formatDecimal(summary.currentUserAverageMultiplier)}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><span className="bm-text-secondary">Resolved ставок</span><strong>{summary.totalResolvedBets}</strong></div>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
